import React, { useState } from 'react';
import { Card, Button, Badge, ListGroup, Form } from 'react-bootstrap';
import { FaBug, FaEye, FaEyeSlash, FaRuler, FaCrosshairs } from 'react-icons/fa';

interface FieldMappingDebuggerProps {
  fields: any[];
  currentPage: number;
  containerDimensions?: { width: number; height: number };
  imageDimensions?: { width: number; height: number };
}

const FieldMappingDebugger: React.FC<FieldMappingDebuggerProps> = ({
  fields,
  currentPage,
  containerDimensions,
  imageDimensions
}) => {
  const [showDebug, setShowDebug] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [selectedField, setSelectedField] = useState<any>(null);

  const pageFields = fields.filter(f => f.page === currentPage);

  return (
    <>
      {/* Debug Toggle Button */}
      <div style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 100
      }}>
        <Button
          variant={showDebug ? 'warning' : 'outline-secondary'}
          size="sm"
          onClick={() => setShowDebug(!showDebug)}
        >
          <FaBug /> {showDebug ? 'Hide' : 'Show'} Debug
        </Button>
      </div>

      {/* Debug Info Panel */}
      {showDebug && (
        <Card style={{
          position: 'absolute',
          top: 50,
          right: 10,
          width: 300,
          maxHeight: '80vh',
          overflowY: 'auto',
          zIndex: 99
        }}>
          <Card.Header>
            <h6 className="mb-0">Field Mapping Debug</h6>
          </Card.Header>
          <Card.Body>
            {/* Container Info */}
            <div className="mb-3">
              <h6><FaRuler /> Dimensions</h6>
              <small className="text-muted">
                Container: {containerDimensions?.width || 'N/A'} × {containerDimensions?.height || 'N/A'}px<br/>
                Image: {imageDimensions?.width || 'N/A'} × {imageDimensions?.height || 'N/A'}px
              </small>
            </div>

            {/* Grid Toggle */}
            <Form.Check
              type="switch"
              label="Show Grid"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              className="mb-3"
            />

            {/* Field List */}
            <h6>Page {currentPage} Fields ({pageFields.length})</h6>
            <ListGroup variant="flush">
              {pageFields.map((field, idx) => (
                <ListGroup.Item
                  key={field.id}
                  action
                  active={selectedField?.id === field.id}
                  onClick={() => setSelectedField(field)}
                  className="small"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <Badge bg={field.source === 'textract' ? 'primary' : 'success'} className="me-1">
                        {field.source}
                      </Badge>
                      <strong>{field.type}</strong>: {field.label}
                    </div>
                    <Badge bg="secondary">{Math.round(field.confidence * 100)}%</Badge>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Pos: ({field.x.toFixed(1)}, {field.y.toFixed(1)}) 
                    Size: {field.width.toFixed(1)}×{field.height.toFixed(1)}%
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>

            {/* Selected Field Details */}
            {selectedField && (
              <div className="mt-3 p-2 bg-light rounded">
                <h6>Selected Field</h6>
                <pre style={{ fontSize: '0.7rem', margin: 0 }}>
                  {JSON.stringify(selectedField, null, 2)}
                </pre>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Grid Overlay */}
      {showDebug && showGrid && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 5
        }}>
          {/* 10% grid lines */}
          {[...Array(11)].map((_, i) => (
            <React.Fragment key={i}>
              {/* Vertical lines */}
              <div style={{
                position: 'absolute',
                left: `${i * 10}%`,
                top: 0,
                width: 1,
                height: '100%',
                backgroundColor: i % 5 === 0 ? 'rgba(255,0,0,0.3)' : 'rgba(0,0,0,0.1)'
              }} />
              {/* Horizontal lines */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: `${i * 10}%`,
                width: '100%',
                height: 1,
                backgroundColor: i % 5 === 0 ? 'rgba(255,0,0,0.3)' : 'rgba(0,0,0,0.1)'
              }} />
              {/* Labels */}
              {i > 0 && (
                <>
                  <div style={{
                    position: 'absolute',
                    left: `${i * 10}%`,
                    top: 5,
                    transform: 'translateX(-50%)',
                    fontSize: '10px',
                    backgroundColor: 'white',
                    padding: '0 2px',
                    borderRadius: 2
                  }}>
                    {i * 10}%
                  </div>
                  <div style={{
                    position: 'absolute',
                    left: 5,
                    top: `${i * 10}%`,
                    transform: 'translateY(-50%)',
                    fontSize: '10px',
                    backgroundColor: 'white',
                    padding: '0 2px',
                    borderRadius: 2
                  }}>
                    {i * 10}%
                  </div>
                </>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Crosshair for selected field */}
      {showDebug && selectedField && selectedField.page === currentPage && (
        <>
          {/* Crosshair lines */}
          <div style={{
            position: 'absolute',
            left: 0,
            top: `${selectedField.y + selectedField.height / 2}%`,
            width: '100%',
            height: 1,
            backgroundColor: 'rgba(255,0,255,0.5)',
            pointerEvents: 'none',
            zIndex: 6
          }} />
          <div style={{
            position: 'absolute',
            left: `${selectedField.x + selectedField.width / 2}%`,
            top: 0,
            width: 1,
            height: '100%',
            backgroundColor: 'rgba(255,0,255,0.5)',
            pointerEvents: 'none',
            zIndex: 6
          }} />
          {/* Center marker */}
          <div style={{
            position: 'absolute',
            left: `${selectedField.x + selectedField.width / 2}%`,
            top: `${selectedField.y + selectedField.height / 2}%`,
            transform: 'translate(-50%, -50%)',
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: 'magenta',
            border: '2px solid white',
            pointerEvents: 'none',
            zIndex: 7
          }} />
        </>
      )}
    </>
  );
};

export default FieldMappingDebugger;